import { useEffect, useState, useMemo } from 'react';
import { getMapIcons, type MapIcon as MapIconType } from '@/lib/api';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { normalizeHexColor } from '@/utils/iconUtils';
import { useTranslation } from 'react-i18next';
import { IconSvgRenderer } from './IconSvgRenderer';

interface IconSelectorProps {
  selectedIconId: string | null;
  onIconSelect: (icon: MapIconType | null) => void;
  onAddIcon: (iconId: string) => void;
  selectedColor?: string;
}

export function IconSelector({
  selectedIconId,
  onIconSelect,
  onAddIcon,
  selectedColor = '#000000',
}: IconSelectorProps) {
  // Normalizovat barvu před použitím - použít useMemo pro správné přepočítávání
  const normalizedColor = useMemo(() => {
    const normalized = normalizeHexColor(selectedColor) || '#000000';
    // Debug: zkontrolovat, zda se barva správně předává
    if (import.meta.env.DEV) {
      // console.log('[IconSelector] selectedColor:', selectedColor, 'normalized:', normalized);
    }
    return normalized;
  }, [selectedColor]);
  const { t } = useTranslation();
  const [icons, setIcons] = useState<MapIconType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);


  useEffect(() => {
    loadIcons();
  }, []);

  const loadIcons = async () => {
    setLoading(true);
    setError(null);

    const result = await getMapIcons();

    if (result.success && result.data) {
      setIcons(result.data);

      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(result.data.map((icon: MapIconType) => icon.category).filter(Boolean))
      ) as string[];
      setCategories(uniqueCategories);
    } else {
      setError(result.error || t('settings.iconsSettings.failedToLoad'));
    }

    setLoading(false);
  };

  const filteredIcons = selectedCategory
    ? icons.filter((icon) => icon.category === selectedCategory)
    : icons;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-boho-cream" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-destructive">{error}</div>
        <Button onClick={loadIcons} variant="outline" size="sm">
          {t('settings.iconsSettings.retry')}
        </Button>
      </div>
    );
  }

  if (icons.length === 0) {
    return (
      <div className="text-sm text-boho-cream/90 py-8 text-center">
        {t('settings.iconsSettings.noIconsAvailable')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category filter */}
      {categories.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-boho-cream">{t('settings.iconsSettings.category')}</div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setSelectedCategory(null)}
              className={cn(selectedCategory === null
                ? 'bg-posterden-red text-white hover:bg-posterden-red/90'
                : 'bg-white/10 text-boho-cream border-white/20 hover:bg-white/15 hover:border-white/30')}
            >
              {t('settings.iconsSettings.all')}
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(selectedCategory === category
                  ? 'bg-posterden-red text-white hover:bg-posterden-red/90'
                  : 'bg-white/10 text-boho-cream border-white/20 hover:bg-white/15 hover:border-white/30')}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Icons grid */}
      <div className="space-y-2">
        <div className="text-sm font-medium text-boho-cream">
          {t('settings.iconsSettings.selectIconHint')}
        </div>
        <ScrollArea className="h-[400px]">
          <div className="grid grid-cols-3 gap-3 p-1">
            {filteredIcons.map((icon) => {
              const isSelected = selectedIconId === icon.id;

              return (
                <button
                  key={icon.id}
                  onClick={() => {
                    onIconSelect(icon);
                    onAddIcon(icon.id);
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all',
                    'hover:bg-white/10 hover:border-posterden-red/50',
                    isSelected
                      ? 'border-posterden-red bg-posterden-red/10 shadow-lg shadow-posterden-red/10'
                      : 'border-white/10 bg-[#252525]'
                  )}
                  title={icon.name}
                >
                  <IconSvgRenderer
                    iconId={icon.id}
                    storageUrl={icon.storage_url}
                    color={normalizedColor}
                    className="w-8 h-8 mb-2"
                    alt={icon.name}
                  />
                  <span className={cn("text-xs text-center line-clamp-2", isSelected ? "text-boho-cream font-medium" : "text-boho-cream/90")}>
                    {icon.name}
                  </span>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div >

      {selectedIconId && (
        <div className="text-xs text-boho-cream/90 bg-white/5 p-2 rounded border border-white/5">
          {t('settings.iconsSettings.selectedIcon', { name: icons.find((i) => i.id === selectedIconId)?.name })}
          <br />
          {t('settings.iconsSettings.clickToPlace')}
        </div>
      )
      }
    </div >
  );
}

